/**
 * Wrapper sottile sopra @microsoft/teams-js v2.
 *
 * Tutte le chiamate Teams sono isolate qui: se l'app gira fuori da Teams
 * (browser di sviluppo) il modulo degrada in "modalità standalone" invece di
 * far fallire l'interfaccia.
 */
import { app, authentication, pages } from '@microsoft/teams-js';
import { acquireApiToken } from './lib/sso.ts';
import { takeToken } from './lib/handoff.ts';

export interface TeamsContext {
  /** true se l'app è ospitata dentro Teams (o un altro host M365). */
  inTeams: boolean;
  userDisplayName: string | null;
  userPrincipalName: string | null;
  tenantId: string | null;
  teamDisplayName: string | null;
  channelDisplayName: string | null;
  locale: string;
  theme: 'default' | 'dark' | 'contrast' | string;
  /** ID dell'app come assegnato dal portale sviluppatori. */
  appId: string | null;
}

export interface TeamsClient {
  context: TeamsContext;
  /** Recupera un token Entra per il backend (SSO). */
  getToken(): Promise<string>;
  /** Naviga a una pagina interna dell'app. */
  navigateTo(pageId: string): Promise<void>;
  /** Registra un handler sui cambi di tema. */
  onThemeChange(handler: (theme: string) => void): void;
}

const standaloneContext: TeamsContext = {
  inTeams: false,
  userDisplayName: null,
  userPrincipalName: null,
  tenantId: null,
  teamDisplayName: null,
  channelDisplayName: null,
  locale: 'it-IT',
  theme: 'default',
  appId: null,
};

function readContext(ctx: app.Context): TeamsContext {
  return {
    inTeams: true,
    userDisplayName: ctx.user?.displayName ?? null,
    userPrincipalName: ctx.user?.userPrincipalName ?? null,
    tenantId: ctx.user?.tenant?.id ?? null,
    teamDisplayName: ctx.team?.displayName ?? null,
    channelDisplayName: ctx.channel?.displayName ?? null,
    locale: ctx.app.locale || 'it-IT',
    theme: ctx.app.theme || 'default',
    appId: ctx.app.appId ? ctx.app.appId.toString() : null,
  };
}

/**
 * Inizializza l'SDK. Fuori da Teams `app.initialize()` non completa: in quel
 * caso restituamo un client standalone con contesto fittizio e token inesistente.
 */
export async function initTeams(): Promise<TeamsClient> {
  try {
    await app.initialize();
  } catch (err) {
    console.warn('[teams] initialize fallito, uso la modalità standalone:', err);
    return {
      context: standaloneContext,
      getToken: async () => {
        throw new Error('Token Entra disponibile solo dentro Microsoft Teams');
      },
      navigateTo: async () => {
        throw new Error('Navigazione non disponibile fuori da Teams');
      },
      onThemeChange: () => undefined,
    };
  }

  const ctx = await app.getContext();

  return {
    context: readContext(ctx),
    async getToken(): Promise<string> {
      return acquireApiToken({
        origin: globalThis.location?.origin ?? '',
        silent: () => authentication.getAuthToken({ silent: true }),
        popup: (url) => authentication.authenticate({ url, width: 600, height: 535 }),
        takeHandoff: () =>
          typeof globalThis.localStorage === 'undefined'
            ? null
            : takeToken(globalThis.localStorage),
      });
    },
    async navigateTo(pageId: string): Promise<void> {
      await pages.currentApp.navigateTo({ pageId });
    },
    onThemeChange(handler: (theme: string) => void): void {
      app.registerOnThemeChangeHandler(handler);
    },
  };
}
